import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Search,
  TrendingUp,
  Globe,
  Settings as SettingsIcon,
  Users,
  Menu,
  User,
  Trophy,
  Target,
  Briefcase,
  Flame,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Sidebar, SidebarHeader, SidebarContent, SidebarNav, SidebarNavItem } from '../ui/sidebar';
import { useAuth } from '../../contexts/AuthContext';
import UserMenu from './UserMenu';

const menuItems = [
  { text: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { text: 'Leading Domains', icon: Trophy, path: '/leading-domains' },
  { text: 'Query Research', icon: Search, path: '/research' },
  { text: 'AI Workflow', icon: TrendingUp, path: '/workflow' },
  { text: 'Optimise Content', icon: TrendingUp, path: '/optimize' },
  { text: 'Opportunities', icon: Target, path: '/opportunities' },
  { text: 'Competitor Intel', icon: Briefcase, path: '/competitor-intelligence' },
  { text: 'Reddit Insights', icon: Flame, path: '/reddit-insights', comingSoon: true },
  { text: 'Websites', icon: Globe, path: '/websites' },
  { text: 'Team', icon: Users, path: '/team' },
  { text: 'Settings', icon: SettingsIcon, path: '/settings' },
];

function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const SidebarComponent = () => (
    <>
      <SidebarHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          <div className="flex flex-col leading-tight">
            <h1 className="text-xl font-bold text-primary">Careerminds Labs</h1>
            <span className="text-xs font-medium text-muted-foreground tracking-wide">GEO Dash</span>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarNav>
          {menuItems.map((item) => (
            item.comingSoon ? (
              <SidebarNavItem
                key={item.text}
                disabled
                className="opacity-60 cursor-not-allowed hover:bg-transparent hover:text-muted-foreground"
                onClick={(e) => e.preventDefault()}
              >
                <item.icon className="h-5 w-5" />
                <span className="flex-1 text-left">{item.text}</span>
                <span className="text-[10px] uppercase tracking-wider bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded">Soon</span>
              </SidebarNavItem>
            ) : (
              <SidebarNavItem
                key={item.path}
                active={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.text}</span>
              </SidebarNavItem>
            )
          ))}
        </SidebarNav>
      </SidebarContent>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar>
          <SidebarComponent />
        </Sidebar>
      </div>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar className="border-0">
            <SidebarComponent />
          </Sidebar>
        </SheetContent>
      </Sheet>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="flex h-16 items-center justify-between border-b bg-background px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h2 className="text-lg font-semibold">
              {menuItems.find(item => item.path === location.pathname)?.text || 'Careerminds Labs'}
            </h2>
          </div>

          <UserMenu user={user} />
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;